/**
 * Helpers for the Sales Team Report's week-column (range) mode.
 */

import React from 'react';
import {
  FunnelMetricKey,
  FunnelPeriod,
  FunnelPeriodMetrics,
  SalesTeamFunnelReportData,
} from '../../../../types/salesTeamFunnel';
import { FUNNEL_COL_COUNT, fmtUnits } from '../../../shared/SalesFunnelRows';

/** Narrow the payload to a range-mode one. */
export function hasPeriods(
  data: SalesTeamFunnelReportData,
): data is SalesTeamFunnelReportData & {
  periods: FunnelPeriod[];
  rangeStartDate: string;
  rangeEndDate: string;
} {
  return Array.isArray(data.periods) && !!data.rangeStartDate && !!data.rangeEndDate;
}

/**
 * Whether to render week columns at all.
 *
 * A single bucket is numerically identical to the Range Total group, so the
 * column would be pure duplication costing a horizontal scroll — show the
 * 11-column table with range labelling instead.
 */
export function weekModeActive(data: SalesTeamFunnelReportData): boolean {
  return hasPeriods(data) && data.periods.length >= 2;
}

/** Total column count for the week table: name + weeks + the standard 10. */
export function weekColCount(periodCount: number): number {
  return periodCount + FUNNEL_COL_COUNT;
}

/**
 * One week cell's content.
 *
 * Renders an em dash — never 0 — when the row has no per-week data at all or
 * fewer buckets than the column list, so "we don't know" never reads as
 * "they sold nothing". Callers MUST index against the top-level `periods`
 * array, not the row's own, or a short row silently shifts every column.
 */
export function metricCell(
  metric: FunnelMetricKey,
  pm: FunnelPeriodMetrics | undefined,
): React.ReactNode {
  if (!pm) return '—';
  const value = pm[metric];
  if (metric === 'sales') return value > 0 ? fmtUnits(value) : '—';
  return value > 0 ? String(value) : '—';
}

/**
 * Whether a bucket's label needs a year suffix — only when the range spans
 * more than one calendar year, which a 92-day range can (Dec → Feb).
 */
export function needsYearSuffix(periods: FunnelPeriod[]): boolean {
  return new Set(periods.map((p) => p.year)).size > 1;
}

/** `Aug W1`, or `Aug W1 '27` when the range crosses a year. */
export function periodLabel(period: FunnelPeriod, withYear: boolean): string {
  return withYear
    ? `${period.label} '${String(period.year).slice(2)}`
    : period.label;
}

/**
 * Print font/column bucket. Above 10 week columns the table no longer fits
 * even legal landscape, so the optional columns are dropped (see index.css).
 */
export function printSizeBucket(periodCount: number): 'sm' | 'md' | 'lg' {
  if (periodCount <= 8) return 'sm';
  if (periodCount <= 10) return 'md';
  return 'lg';
}

/**
 * True when the row has no activity IN THE RANGE.
 *
 * Deliberately only the four range metrics. `activeLeads` is a point-in-time
 * count as of today and `apptsNext5` is the forward outlook — neither describes
 * the selected range, so including them would leave rows visible that the
 * checkbox ("Hide salespeople with no activity in this range") promised to
 * hide. Note such a row exists at all only because buildSalesFunnel keeps rows
 * with active leads or upcoming appointments; hiding it here is correct for an
 * opt-in filter whose label is about the range.
 */
export function isInactiveRow(row: {
  leads: number; visits: number; apptsPast: number; sales: number;
}): boolean {
  return !row.leads && !row.visits && !row.apptsPast && !row.sales;
}
